import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Table, Button, Modal, Row, Col, Upload, message, Space, Divider, Tooltip, Input } from "antd";
import { DownloadOutlined, UploadOutlined, FilePdfOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { SquarePen } from "lucide-react";
import { useFetchDesignerJobcardsQuery, useUploadJobcardImagesMutation, useUpdateJobcardImagesMutation, useUpdateJobcardMutation } from "features/store";
import { url as API_BASE_URL } from "components/common/api";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { roleSelector } from "features/jobinv/JobCardSlice";
import { getImage, getImageUploader } from '../helper'
import { downloadS3File } from '../utils/downloadFile'
import { formatImageCreatedAt } from '../utils/timezoneUtils'
import { setDigitizerPage } from "features/auth/userSlice";
import { STAGES } from "components/common/Constants";
const SIDES = ["Front", "Right Middle", "Left Middle", "Back", "Velcro", "Visor"];

const TopBar = () => (
  <div style={{
    height: 56,
    display: "flex",
    alignItems: "center",
    padding: "0 16px",
    borderBottom: "1px solid #f0f0f0",
    background: "#fff",
    position: "sticky",
    top: 0,
    zIndex: 10,
  }} />
);

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const DigitizerJobcards = () => {
  const [pageSize, setPageSize] = useState(20);
  const [searchTitle, setSearchTitle] = useState("");
  const [searchJobcard, setSearchJobcard] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const dispatch = useDispatch();
  const digitizerPage = useSelector((state) => state.user.digitizer_page) || 1;
  const { data, isFetching, refetch } = useFetchDesignerJobcardsQuery({
    page: digitizerPage,
    page_size: pageSize,
    title: searchTitle || undefined,
    jobcard_id: searchJobcard || undefined,
  });
  const navigate = useNavigate();
  const [uploadJobcardImages, uploadInfo] = useUploadJobcardImagesMutation();
  const [updateJobcard, updateInfo] = useUpdateJobcardMutation();

  const [activeJobcard, setActiveJobcard] = useState(null);
  const [filesBySide, setFilesBySide] = useState({});
  const [titlesBySide, setTitlesBySide] = useState({});
  const [descriptionsBySide, setDescriptionsBySide] = useState({});
  const [titleInputs, setTitleInputs] = useState({});
  const [descInputs, setDescInputs] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [jobToDisable, setJobToDisable] = useState(null);
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [selectedJobcardForNotes, setSelectedJobcardForNotes] = useState(null);
  const [notesText, setNotesText] = useState("");
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const role = useSelector(roleSelector);
  const user = JSON.parse(localStorage.getItem("user") || "null");
  const isDegitizer = role === "degitizer" || user?.roll === "degitizer";
  const [updateJobcardImages, updateImagesInfo] = useUpdateJobcardImagesMutation();

  const totalJobcards = data?.count ?? 0;

  useEffect(() => {
    if (!totalJobcards) {
      if (digitizerPage !== 1) {
        dispatch(setDigitizerPage(1));
      }
      return;
    }

    const maxPage = Math.max(1, Math.ceil(totalJobcards / pageSize));
    if (digitizerPage > maxPage) {
      dispatch(setDigitizerPage(maxPage));
    }
  }, [totalJobcards, pageSize, digitizerPage, dispatch]);

  const downloadImage = async (url, suggestedName) => {
    try {
      await downloadS3File(url, suggestedName);
    } catch (e) {
      message.error("Download failed");
    }
  };

  const getExtFromPath = (path) => {
    if (!path) return "";
    try {
      const clean = String(path).split("?")[0];
      const lastDot = clean.lastIndexOf(".");
      return lastDot > -1 ? clean.slice(lastDot) : "";
    } catch {
      return "";
    }
  };

  const jobcards = data?.results || [];

  // Priority sort function matching backend logic:
  // 1. First by priority_number (ascending, nulls last)
  // 2. Then by priority (descending, nulls last) - Critical (4) > High (3) > Medium (1) > Low (2/0)
  // 3. Finally by id (ascending)
  const sortByPriority = (a, b) => {
    // Compare priority_number first (ascending, nulls last)
    const aPriorityNum = a.priority_number ?? Number.MAX_SAFE_INTEGER;
    const bPriorityNum = b.priority_number ?? Number.MAX_SAFE_INTEGER;
    if (aPriorityNum !== bPriorityNum) {
      return aPriorityNum - bPriorityNum;
    }

    // Then compare priority (descending, nulls last)
    const aPriority = a.priority ?? -1;
    const bPriority = b.priority ?? -1;
    if (aPriority !== bPriority) {
      return bPriority - aPriority; // Descending order
    }

    // Finally by id (ascending)
    return (a.id ?? 0) - (b.id ?? 0);
  };

  // Sort jobcards by priority before rendering
  const sortedJobcards = useMemo(() => {
    return [...jobcards].sort(sortByPriority);
  }, [jobcards]);

  // No longer used for Embroidery rejections
  const isJobcardRejected = (record) => {
    return false;
  };

  useEffect(() => {
    // Keep the input in sync with active filters when they change externally (e.g. pagination reset)
    if (searchJobcard) {
      setSearchValue(searchJobcard);
    } else {
      setSearchValue(searchTitle);
    }
  }, [searchTitle, searchJobcard]);

  const handleSearch = useCallback((rawValue) => {
    const valueToUse = rawValue !== undefined ? rawValue : searchValue;
    const trimmedValue = (valueToUse || "").trim();
    dispatch(setDigitizerPage(1));
    if (!trimmedValue) {
      setSearchTitle("");
      setSearchJobcard("");
      setSearchValue("");
      return;
    }

    // Updated pattern to accept alphanumeric serial numbers (e.g., "123", "123A", "123B")
    // Pattern: starts with digits, optionally followed by letters, supports comma-separated values
    const jobcardPattern = /^[0-9]+[A-Z]*(?:\s*,\s*[0-9]+[A-Z]*)*$/i;
    if (jobcardPattern.test(trimmedValue)) {
      const sanitizedJobcard = trimmedValue
        ? trimmedValue
          .split(",")
          .map((part) => part.trim())
          .filter(Boolean)
          .join(",")
        : "";
      setSearchJobcard(sanitizedJobcard);
      setSearchTitle("");
      setSearchValue(sanitizedJobcard);
    } else {
      setSearchTitle(trimmedValue);
      setSearchJobcard("");
      setSearchValue(trimmedValue);
    }
  }, [searchValue]);

  const handleResetSearch = useCallback(() => {
    if (!searchTitle && !searchJobcard && !searchValue) {
      return;
    }
    dispatch(setDigitizerPage(1));
    setSearchTitle("");
    setSearchJobcard("");
    setSearchValue("");
  }, [searchTitle, searchJobcard, searchValue, dispatch]);

  const handleTableChange = (paginationConfig) => {
    const { current = 1, pageSize: newPageSize = pageSize } = paginationConfig;

    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
      dispatch(setDigitizerPage(1));
      return;
    }

    if (current !== digitizerPage) {
      dispatch(setDigitizerPage(current));
    }
  };

  const withBase = (src) => (src?.startsWith("http") ? src : `${API_BASE_URL}${src?.startsWith("/") ? "" : "/"}${src || ""}`);

  const getFilenameFromUrl = (rawPath) => {
    if (!rawPath) return "";
    try {
      const clean = String(rawPath).split("?")[0];
      const parts = clean.split("/");
      return parts[parts.length - 1] || "";
    } catch {
      return "";
    }
  };

  const getKey = (jobId, side, field) => `${jobId}__${side}__${field}`;

  const handleInputChange = (jobId, side, field, value) => {
    const key = getKey(jobId, side, field);
    if (field === "title") {
      setTitleInputs(prev => ({ ...prev, [key]: value }));
    } else {
      setDescInputs(prev => ({ ...prev, [key]: value }));
    }
  };

  const handleSaveField = async (record, side, field) => {
    try {
      const allImages = record.jobcard_images || [];
      // We can only update metadata for an existing digitizer image for this side
      const digitizerImage = allImages.find(img => img && img.is_degitizer_image && img.side === side);
      // Read current input value
      const titleKey = getKey(record.id, side, "title");
      const descKey = getKey(record.id, side, "description");
      const titleVal = field === "title" ? (titleInputs[titleKey] ?? "") : (titleInputs[titleKey] ?? digitizerImage?.title ?? "");
      const descVal = field === "description" ? (descInputs[descKey] ?? "") : (descInputs[descKey] ?? digitizerImage?.description ?? "");

      if (!digitizerImage) {
        // No digitizer image yet: update the designer image's title via bulk update API
        const allImages = record.jobcard_images || [];
        const designerImage = allImages.find(img => img && !img.is_degitizer_image && img.side === side);
        if (!designerImage) {
          message.error("Designer side not found to attach title.");
          return;
        }
        const designerRaw = getImage(designerImage);
        const designerFilename = getFilenameFromUrl(designerRaw) || `designer_${String(side || "").replace(/\s+/g, "_")}.png`;
        await updateJobcardImages({
          metadata: [{
            id: designerImage.id,
            filename: designerFilename,
            side,
            created_by: user?.username || user?.email || "unknown",
            title: titleVal,
            description: null,
          }],
          files: [],
        }).unwrap();
        message.success("Title saved");
        setTitleInputs(prev => {
          const next = { ...prev };
          delete next[titleKey];
          return next;
        });
        refetch();
        return;
      }

      // There is an existing digitizer image; update its metadata on backend
      const rawSrc = getImage(digitizerImage);
      const filename = getFilenameFromUrl(rawSrc) || `jobcard_${String(side || "").replace(/\s+/g, "_")}.png`;
      await updateJobcardImages({
        metadata: [{
          id: digitizerImage.id,
          filename,
          side,
          created_by: user?.username || user?.email || "unknown",
          title: titleVal,
          description: descVal,
        }],
        files: [],
      }).unwrap();
      message.success(`${field === "title" ? "Title" : "Description"} saved`);
      if (field === "title") {
        setTitleInputs(prev => {
          const next = { ...prev };
          delete next[titleKey];
          return next;
        });
      } else {
        setDescInputs(prev => {
          const next = { ...prev };
          delete next[descKey];
          return next;
        });
      }
      refetch();
    } catch (e) {
      message.error("Failed to save");
    }
  };

  const columns = useMemo(() => ([
    {
      title: "Jobcard No",
      dataIndex: "serial_number",
      key: "serial_number",
      width: 80,
      align: "center",
      render: (serial_number, record) => (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4, position: "relative" }}>
          <span>{serial_number || record.id || "—"}</span>
          {record?.priority_number != null && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "14px",
                height: "14px",
                padding: "0 3px",
                fontSize: "9px",
                fontWeight: 600,
                borderRadius: "3px",
                background: "#1677ff",
                color: "#ffffff",
                lineHeight: 1,
                border: "1px solid #ffffff",
                boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
              }}
              title={`Priority Number: ${record.priority_number}`}
            >
              {record.priority_number}
            </span>
          )}
        </div>
      )
    },
    {
      title: "Images", key: "images", align: "center", render: (_, record) => {
        const imgs = (record.jobcard_images || []).filter(Boolean);
        return (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
            {imgs.map((img) => {
              const raw = getImage(img);
              const src = withBase(raw);
              const lower = String(raw || "").toLowerCase();
              const isPdf = lower.endsWith('.pdf');
              const isImage = /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(lower);
              const uploader = getImageUploader(img);
              // Color coding: Designer = blue, Digitizer = green, Embroidary = purple
              const borderColor = uploader === "Designer" ? "#1890ff" : uploader === "Embroidary" ? "#722ed1" : "#52c41a";
              return (
                <div
                  key={img.id}
                  onClick={() => {
                    setSelectedImage({ ...img, imageUrl: src, rawSrc: raw });
                    setImageModalOpen(true);
                  }}
                  style={{
                    position: "relative",
                    height: 80,
                    borderRadius: 12,
                    overflow: "hidden",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    background: "#f0f0f0",
                    cursor: "pointer",
                    border: `2px solid ${borderColor}`,
                  }}
                >
                  <span style={{ position: "absolute", top: 6, left: 6, background: "rgba(0,0,0,0.55)", color: "#fff", fontSize: 10, padding: "2px 8px", borderRadius: 999, zIndex: 1 }}>{img.side}</span>
                  {uploader && (
                    <span style={{ position: "absolute", top: 6, right: 6, background: borderColor, color: "#fff", fontSize: 9, padding: "2px 6px", borderRadius: 999, zIndex: 1, fontWeight: 600 }}>
                      {uploader === "Embroidary" ? "Emb" : uploader === "Digitizer" ? "Dig" : "Des"}
                    </span>
                  )}
                  {isPdf ? (
                    <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <FilePdfOutlined style={{ fontSize: 32, color: "#cf1322" }} />
                    </div>
                  ) : isImage ? (
                    <img src={src} alt={img.side} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                  ) : (
                    <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", background: "#f7f7f7" }}>
                      <span role="img" aria-label="file" style={{ fontSize: 28, lineHeight: 1 }}>🗂️</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      }
    },
    {
      title: "Information",
      key: "information",
      width: 280,
      align: "left",
      render: (_, record) => {
        // Dynamic side-based inputs shown vertically (Top to Down order)
        const sideOrder = ["Front", "Right Middle", "Left Middle", "Back", "Velcro", "Visor"];
        const imgs = (record.jobcard_images || []).filter(Boolean);
        const designerSides = new Set(
          imgs.filter(img => img && img.side && !img.is_degitizer_image).map(img => img.side)
        );
        const digitizerBySide = {};
        const designerBySide = {};
        const embroidaryBySide = {};
        imgs.filter(img => img && img.side && img.is_degitizer_image).forEach(img => { digitizerBySide[img.side] = img; });
        imgs.filter(img => img && img.side && !img.is_degitizer_image).forEach(img => { designerBySide[img.side] = img; });

        // Get embroidary images
        imgs.filter(img => img && img.side && getImageUploader(img) === "Embroidary").forEach(img => { embroidaryBySide[img.side] = img; });

        const rows = sideOrder
          .filter(side => designerSides.has(side))
          .map((side) => {
            const dImg = digitizerBySide[side];
            const embroidaryImg = embroidaryBySide[side];
            const titleKey = getKey(record.id, side, "title");
            const descKey = getKey(record.id, side, "description");
            const titleVal = titleInputs[titleKey] ?? (dImg?.title ?? designerBySide[side]?.title ?? "");
            const descVal = descInputs[descKey] ?? (dImg?.description || "");
            // Show embroidary description
            const embroidaryTitle = embroidaryImg ? embroidaryImg.title : null;
            const embroidaryDescription = embroidaryImg ? embroidaryImg.description : null;
            const hasEmbroidaryMeta = Boolean(embroidaryTitle || embroidaryDescription);
            const canSave = true;
            return (
              <div key={`${record.id}-${side}`} style={{ display: "flex", flexDirection: "column", gap: 6, paddingBottom: 8, borderBottom: "1px dashed #f0f0f0", marginBottom: 8 }}>
                <div style={{ fontWeight: 600, color: "#333" }}>{side}</div>
                <div style={{ display: "flex", gap: 6 }}>
                  <Input
                    size="small"
                    placeholder="Title"
                    value={titleVal}
                    onChange={(e) => handleInputChange(record.id, side, "title", e.target.value)}
                    style={{ flex: 1 }}
                  />
                  <Button
                    size="small"
                    type="primary"
                    disabled={updateImagesInfo.isLoading}
                    loading={updateImagesInfo.isLoading}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSaveField(record, side, "title");
                    }}
                  >
                    Save
                  </Button>
                </div>
                {/* Show embroidary metadata */}
                {hasEmbroidaryMeta && (
                  <div style={{
                    padding: "8px 10px",
                    background: "#fff1f0",
                    borderRadius: 6,
                    border: "1px solid #ffccc7",
                    fontSize: 11,
                    color: "#722ed1",
                    fontWeight: 500,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word"
                  }}>
                    <div style={{ fontSize: 10, fontWeight: 600, color: "#cf1322", marginBottom: 4 }}>
                      Embroidery Note:
                    </div>
                    {embroidaryTitle && (
                      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: embroidaryDescription ? 4 : 0, color: "#262626" }}>
                        {embroidaryTitle}
                      </div>
                    )}
                    {embroidaryDescription}
                  </div>
                )}
              </div>
            );
          });

        return rows.length ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 12 }}>
            {rows}
          </div>
        ) : (
          <div style={{ color: "#999", fontStyle: "italic" }}>No information</div>
        );
      }
    },
    {
      title: "Notes",
      key: "notes",
      width: 120,
      align: "left",
      render: (_, record) => {
        const hasNotes = record.notes && String(record.notes).trim().length > 0;
        const cleanedNotes = (() => {
          const raw = record.notes;
          if (!raw) return "";
          try {
            const obj = JSON.parse(String(raw));
            if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
              const filtered = { ...obj };
              delete filtered.technique_map;
              delete filtered.eyelet_details;
              delete filtered.TECHNIQUE_MAP;
              delete filtered.EYELET_DETAILS;
              return Object.keys(filtered).length > 0 ? JSON.stringify(filtered, null, 2) : "";
            }
            return String(raw);
          } catch (e) {
            return String(raw);
          }
        })();

        return (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {hasNotes ? (
              <>
                <div style={{
                  flex: 1,
                  fontSize: 12,
                  color: "#333",
                  wordBreak: "break-word",
                  paddingRight: 8
                }}>
                  {cleanedNotes || ""}
                </div>
                <Tooltip title="Edit notes">
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedJobcardForNotes(record);
                      setNotesText(cleanedNotes || "");
                      setNotesModalOpen(true);
                    }}
                    role="button"
                    aria-label="Edit notes"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      cursor: "pointer",
                      flexShrink: 0
                    }}
                  >
                    <SquarePen size={18} color="#22c55e" />
                  </span>
                </Tooltip>
              </>
            ) : (
              <Tooltip title="Add notes">
                <Button
                  type="default"
                  shape="circle"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setSelectedJobcardForNotes(record);
                    setNotesText(cleanedNotes || "");
                    setNotesModalOpen(true);
                  }}
                />
              </Tooltip>
            )}
          </div>
        );
      }
    },
    {
      title: "Action", key: "action", width: 220, align: "center", render: (_, record) => (
        <Space>
          <Tooltip title="Upload Your Images">
            <Button
              type="default"
              onClick={() => {
                setActiveJobcard(record);
                setFilesBySide({});
                setTitlesBySide({});
                setDescriptionsBySide({});
              }}
            >
              Upload
            </Button>
          </Tooltip>
          <Tooltip
            title={(() => {
              const imgs = record?.jobcard_images || [];
              const embroidaryDoneSides = new Set(
                imgs.filter(img => img && img.side && getImageUploader(img) === "Embroidary").map(img => img.side)
              );
              const designerSides = new Set(
                imgs.filter(img => img && img.side && getImageUploader(img) === "Designer" && !embroidaryDoneSides.has(img.side)).map(img => img.side)
              );
              const digitizerSides = new Set(
                imgs.filter(img => img && img.side && img.is_degitizer_image).map(img => img.side)
              );
              const allRequiredDone = Array.from(designerSides).every(side => digitizerSides.has(side));
              return allRequiredDone
                ? "Mark this job as done and it will be removed from work list"
                : "Upload all sides sent by the designer before marking as done";
            })()}
          >
            <Button
              type="primary"
              danger
              disabled={(() => {
                const imgs = record?.jobcard_images || [];
                const embroidaryDoneSides = new Set(
                  imgs.filter(img => img && img.side && getImageUploader(img) === "Embroidary").map(img => img.side)
                );
                const designerSides = new Set(
                  imgs.filter(img => img && img.side && getImageUploader(img) === "Designer" && !embroidaryDoneSides.has(img.side)).map(img => img.side)
                );
                const digitizerSides = new Set(
                  imgs.filter(img => img && img.side && img.is_degitizer_image).map(img => img.side)
                );
                return !Array.from(designerSides).every(side => digitizerSides.has(side));
              })()}
              loading={updateInfo.isLoading && jobToDisable?.id === record.id}
              onClick={() => { setJobToDisable(record); setConfirmOpen(true); }}
            >
              Digitizer Done
            </Button>
          </Tooltip>
        </Space>
      )
    },
  ]), [titleInputs, descInputs, updateImagesInfo.isLoading]);

  const openUpdate = (jobcard) => {
    setActiveJobcard(jobcard);
    setFilesBySide({});
  };

  const closeUpdate = () => {
    setActiveJobcard(null);
    setFilesBySide({});
  };

  const handleFileChange = (side, file) => {
    setFilesBySide(prev => ({ ...prev, [side]: file }));
    return false; // prevent auto upload
  };

  const doUpload = async () => {
    if (!activeJobcard) return;
    const selectedFiles = Object.entries(filesBySide).filter(([, f]) => !!f);
    if (selectedFiles.length === 0) {
      message.warning("Please select at least one side to upload");
      return;
    }

    // Allow upload ONLY for sides that came from designer
    const designerSides = new Set(
      (activeJobcard.jobcard_images || [])
        .filter((img) => img && img.side && getImageUploader(img) === "Designer")
        .map((img) => img.side)
    );
    const invalidSides = selectedFiles.map(([side]) => side).filter((side) => !designerSides.has(side));
    if (invalidSides.length) {
      message.error(`You can only upload for sides sent by designer. Invalid: ${invalidSides.join(", ")}`);
      return;
    }

    // Create a map of existing digitizer images by side
    // Get the most recent digitizer image for each side (in case there are multiple)
    const existingDigitizerImages = {};
    (activeJobcard.jobcard_images || [])
      .filter((img) => img && img.side && img.is_degitizer_image)
      .sort((a, b) => new Date(b.created_at || b.updated_at || 0) - new Date(a.created_at || a.updated_at || 0))
      .forEach((img) => {
        // Only keep the most recent one per side
        if (!existingDigitizerImages[img.side]) {
          existingDigitizerImages[img.side] = img;
        }
      });

    const username = user?.username || user?.email || "unknown";

    // Split selected files into update and create groups
    // If there's an existing digitizer image for that side, we'll UPDATE it (replace the old one)
    // If not, we'll CREATE a new one
    const filesToUpdate = [];
    const filesToCreate = [];

    for (const [side, f] of selectedFiles) {
      if (existingDigitizerImages[side] && existingDigitizerImages[side].id) {
        // Update existing digitizer image - this will replace the old image
        filesToUpdate.push([side, f]);
      } else {
        // Create new digitizer image
        filesToCreate.push([side, f]);
      }
    }

    try {
      // Handle updates (PATCH) - replaces existing digitizer images
      // When updating, the old digitizer image file will be replaced with the new one
      if (filesToUpdate.length > 0) {
        const updateMetadata = [];
        const updateFiles = [];

        for (const [side, f] of filesToUpdate) {
          const originalName = f?.name || "";
          const dotIndex = originalName.lastIndexOf(".");
          const ext = dotIndex > -1 ? originalName.slice(dotIndex) : "";
          const safeSide = side.replace(/\s+/g, "_");
          const filename = `jobcard_${safeSide}${ext}`;
          const renamed = new File([f], filename, { type: f.type, lastModified: f.lastModified });

          const existingImage = existingDigitizerImages[side];
          // Update the existing digitizer image - this replaces the old image file
          updateMetadata.push({
            id: existingImage.id,
            filename,
            side,
            created_by: username,
            title: titlesBySide[side] || null,
            description: descriptionsBySide[side] || null,
          });
          updateFiles.push(renamed);
        }

        // This update will replace the old digitizer image with the new one
        await updateJobcardImages({
          metadata: updateMetadata,
          files: updateFiles,
        }).unwrap();
      }

      // Handle new uploads (POST)
      if (filesToCreate.length > 0) {
        const createMetadata = [];
        const createFiles = [];

        for (const [side, f] of filesToCreate) {
          const originalName = f?.name || "";
          const dotIndex = originalName.lastIndexOf(".");
          const ext = dotIndex > -1 ? originalName.slice(dotIndex) : "";
          const safeSide = side.replace(/\s+/g, "_");
          const filename = `jobcard_${safeSide}${ext}`;
          // Create a new File with the exact filename to ensure backend can match it
          const renamed = new File([f], filename, { type: f.type, lastModified: f.lastModified });

          // Verify the file name is set correctly
          if (renamed.name !== filename) {
            console.warn(`File name mismatch: expected ${filename}, got ${renamed.name}`);
          }

          createMetadata.push({
            filename,
            side,
            title: titlesBySide[side] || null,
            description: descriptionsBySide[side] || null,
          });
          createFiles.push(renamed);
        }

        // Debug: Log what we're sending
        console.log('Uploading digitizer images:', {
          metadataCount: createMetadata.length,
          filesCount: createFiles.length,
          metadata: createMetadata.map(m => ({ filename: m.filename, side: m.side })),
          fileNames: createFiles.map(f => f.name)
        });

        await uploadJobcardImages({
          job_card_id: activeJobcard.id,
          is_degitizer_image: true,
          metadata: createMetadata,
          files: createFiles,
        }).unwrap();
      }
      message.success("Images updated");
      setFilesBySide({});
      setTitlesBySide({});
      setDescriptionsBySide({});
      closeUpdate();
      refetch();
    } catch (e) {
      message.error("Upload failed");
    }
  };

  return (
    <div style={{ background: "#f5f5f5", minHeight: "100vh" }}>
      <TopBar />
      <div style={{ padding: 24 }}>
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, marginRight: 30, flexWrap: "wrap" }}>
          <Input.Search
            allowClear
            placeholder="Search by title, jobcard number, or serial number"
            size="large"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onSearch={(val) => handleSearch(val)}
            enterButton={
              <Button type="primary" style={{ height: 40, padding: "0 28px" }} loading={isFetching}>
                Search
              </Button>
            }
            style={{ width: 480 }}
          />
          <Button
            size="large"
            onClick={handleResetSearch}
            disabled={!searchTitle && !searchJobcard && !searchValue}
            style={{ padding: "0 24px" }}
          >
            Reset
          </Button>
          <Tooltip title="Refresh">
            <Button
              size="large"
              type="text"
              icon={<ReloadOutlined style={{ fontSize: '20px', color: '#1677ff' }} />}
              onClick={() => refetch()}
              loading={isFetching}
              className="refresh-button"
              style={{
                height: 40,
                padding: "0 16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            />
          </Tooltip>
        </div>
        <style>
          {`
            /* add vertical gap between table rows */
            .digitizer-table .ant-table-container table { border-spacing: 0 12px !important; border-collapse: separate !important; }
            .digitizer-table .ant-table-tbody > tr > td { background: #fff; border: 1px solid #f0f0f0; }
            .digitizer-table .ant-table-tbody > tr:hover > td { background: #fafafa; }
            /* Rejected jobcards - light red background */
            .digitizer-table .ant-table-tbody > tr.rejected-jobcard > td { background: #fff1f0 !important; border-color: #ffccc7 !important; }
            .digitizer-table .ant-table-tbody > tr.rejected-jobcard:hover > td { background: #ffe7e5 !important; }
            
            /* Priority styles - matching main jobcards table */
            .digitizer-table .priority-0 {
              border-left: 4px solid grey;
            }
            .digitizer-table .priority-0:hover td {
              background: grey !important;
            }
            
            .digitizer-table .priority-1 {
              border-left: 4px solid #bee835;
            }
            .digitizer-table .priority-1 td {
              background: #bee835 !important;
              color: #000000 !important;
            }
            .digitizer-table .priority-1 td a:not(.ant-btn):not(.ant-select-selector),
            .digitizer-table .priority-1 td span:not(.ant-select-selection-item):not([style*="background"]):not([style*="backgroundColor"]):not(button span):not(.ant-btn span),
            .digitizer-table .priority-1 td div:not([class*="Button"]):not([class*="ant-select"]),
            .digitizer-table .priority-1 td p,
            .digitizer-table .priority-1 td label {
              color: #000000 !important;
            }
            .digitizer-table .priority-1:hover td {
              background: #bee835 !important;
            }
            
            .digitizer-table .priority-2 {
              border-left: 4px solid #46e07e;
            }
            .digitizer-table .priority-2 td {
              background: #46e07e !important;
              color: #000000 !important;
            }
            .digitizer-table .priority-2 td a:not(.ant-btn):not(.ant-select-selector),
            .digitizer-table .priority-2 td span:not(.ant-select-selection-item):not([style*="background"]):not([style*="backgroundColor"]):not(button span):not(.ant-btn span),
            .digitizer-table .priority-2 td div:not([class*="Button"]):not([class*="ant-select"]),
            .digitizer-table .priority-2 td p,
            .digitizer-table .priority-2 td label {
              color: #000000 !important;
            }
            .digitizer-table .priority-2:hover td {
              background: #46e07e !important;
            }
            
            .digitizer-table .priority-3 {
              border-left: 4px solid #5a46e0;
            }
            .digitizer-table .priority-3 td {
              background: #5a46e0 !important;
              color: #ffffff !important;
            }
            .digitizer-table .priority-3 td a:not(.ant-btn):not(.ant-select-selector),
            .digitizer-table .priority-3 td span:not(.ant-select-selection-item):not([style*="background"]):not([style*="backgroundColor"]):not(button span):not(.ant-btn span),
            .digitizer-table .priority-3 td div:not([class*="Button"]):not([class*="ant-select"]),
            .digitizer-table .priority-3 td p,
            .digitizer-table .priority-3 td label {
              color: #ffffff !important;
            }
            .digitizer-table .priority-3:hover td {
              background: #5a46e0 !important;
            }
            
            .digitizer-table .priority-4 {
              border-left: 4px solid #f54033;
            }
            .digitizer-table .priority-4 td {
              background: #f54033 !important;
              color: #ffffff !important;
            }
            .digitizer-table .priority-4 td a:not(.ant-btn):not(.ant-select-selector),
            .digitizer-table .priority-4 td span:not(.ant-select-selection-item):not([style*="background"]):not([style*="backgroundColor"]):not(button span):not(.ant-btn span),
            .digitizer-table .priority-4 td div:not([class*="Button"]):not([class*="ant-select"]),
            .digitizer-table .priority-4 td p,
            .digitizer-table .priority-4 td label {
              color: #ffffff !important;
            }
            .digitizer-table .priority-4:hover td {
              background: #f54033 !important;
            }
            
            /* Refresh button hover effect with blur */
            .refresh-button {
              transition: all 0.3s ease;
            }
            .refresh-button:hover {
              background: rgba(255, 255, 255, 0.7) !important;
              backdrop-filter: blur(8px);
              -webkit-backdrop-filter: blur(8px);
            }
            .refresh-button .anticon {
              color: #1677ff !important;
            }
          `}
        </style>
        <Table
          className="digitizer-table"
          rowKey="id"
          loading={isFetching}
          style={{ margin: "16px 32px 0 32px" }}
          columns={columns}
          dataSource={sortedJobcards}
          rowClassName={(record) => {
            const classes = [];
            // Add priority class if priority exists
            if (record?.priority != null) {
              classes.push(`priority-${record.priority}`);
            }
            // Add rejected class if jobcard is rejected
            if (isJobcardRejected(record)) {
              classes.push("rejected-jobcard");
            }
            return classes.join(" ");
          }}
          pagination={{
            current: digitizerPage,
            pageSize,
            total: totalJobcards,
            showSizeChanger: true,
            pageSizeOptions: PAGE_SIZE_OPTIONS.map(String),
            showLessItems: true,
          }}
          onChange={handleTableChange}
        />
      </div>

      {/* Notes Modal */}
      <Modal
        title={`${selectedJobcardForNotes?.notes ? 'Edit' : 'Add'} Notes - Jobcard #${selectedJobcardForNotes?.serial_number || selectedJobcardForNotes?.id || ''}`}
        open={notesModalOpen}
        onCancel={() => {
          setNotesModalOpen(false);
          setSelectedJobcardForNotes(null);
          setNotesText("");
        }}
        footer={[
          <Button key="cancel" onClick={() => {
            setNotesModalOpen(false);
            setSelectedJobcardForNotes(null);
            setNotesText("");
          }}>
            Cancel
          </Button>,
          <Button
            key="add"
            type="primary"
            loading={updateInfo.isLoading}
            onClick={async () => {
              if (!selectedJobcardForNotes) return;
              try {
                await updateJobcard({
                  id: selectedJobcardForNotes.id,
                  notes: notesText,
                }).unwrap();
                message.success("Notes saved successfully");
                setNotesModalOpen(false);
                setSelectedJobcardForNotes(null);
                setNotesText("");
                refetch();
              } catch (e) {
                message.error("Failed to save notes");
              }
            }}
          >
            {selectedJobcardForNotes?.notes ? "UPDATE" : "ADD NOTE"}
          </Button>,
        ]}
        width={600}
      >
        <div style={{ marginBottom: 8, fontWeight: 500 }}>Notes</div>
        <Input.TextArea
          placeholder="Enter notes here..."
          value={notesText}
          onChange={(e) => setNotesText(e.target.value)}
          rows={6}
        />
      </Modal>

      {/* Image Preview Modal */}
      <Modal
        title={selectedImage ? `${selectedImage.side} - Jobcard #${(jobcards.find(j => j.jobcard_images?.some(img => img.id === selectedImage.id))?.serial_number || jobcards.find(j => j.jobcard_images?.some(img => img.id === selectedImage.id))?.id) || ''}` : "Image Preview"}
        open={imageModalOpen}
        onCancel={() => {
          setImageModalOpen(false);
          setSelectedImage(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setImageModalOpen(false);
            setSelectedImage(null);
          }}>
            Close
          </Button>,
          <Button
            key="download"
            type="primary"
            icon={<DownloadOutlined />}
            onClick={() => {
              if (!selectedImage) return;
              const ext = getExtFromPath(selectedImage.rawSrc) || "";
              const jobcard = jobcards.find(j => j.jobcard_images?.some(img => img.id === selectedImage.id));
              const jobcardSerial = jobcard?.serial_number || jobcard?.id || 'image';
              downloadImage(selectedImage.imageUrl, `${jobcardSerial}_${selectedImage.side}${ext}`);
            }}
          >
            Download
          </Button>,
        ]}
        width={800}
      >
        {selectedImage && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16, minHeight: 400 }}>
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%" }}>
              {(() => {
                const lower = String(selectedImage.rawSrc || "").toLowerCase();
                const isPdf = lower.endsWith('.pdf');
                const isImage = /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(lower);

                if (isPdf) {
                  return (
                    <div style={{ width: "100%", maxWidth: "100%", height: "70vh" }}>
                      <iframe
                        src={`${selectedImage.imageUrl}#toolbar=1&navpanes=0&scrollbar=1`}
                        title={`PDF - ${selectedImage.side}`}
                        style={{
                          width: "100%",
                          height: "100%",
                          border: "none",
                          borderRadius: 8,
                          background: "#fff"
                        }}
                      />
                    </div>
                  );
                } else if (isImage) {
                  return (
                    <img
                      src={selectedImage.imageUrl}
                      alt={selectedImage.side}
                      style={{
                        maxWidth: "620px",
                        maxHeight: "50vh",
                        objectFit: "contain",
                        borderRadius: 8
                      }}
                    />
                  );
                } else {
                  return (
                    <div style={{ textAlign: "center" }}>
                      <span role="img" aria-label="file" style={{ fontSize: 80, lineHeight: 1 }}>🗂️</span>
                      <div style={{ marginTop: 16, fontSize: 16, fontWeight: 600 }}>File Preview Not Available</div>
                    </div>
                  );
                }
              })()}
            </div>

            {(selectedImage.title || selectedImage.description) && (
              <div style={{ width: "100%", maxWidth: 640, textAlign: "center", padding: "12px 16px", background: "#fafafa", borderRadius: 12, border: "1px solid #f0f0f0" }}>
                {selectedImage.title && (
                  <div style={{ fontSize: 18, fontWeight: 600, marginBottom: selectedImage.description ? 8 : 0 }}>
                    {selectedImage.title}
                  </div>
                )}
                {selectedImage.description && (
                  <div style={{ fontSize: 14, color: "#595959", whiteSpace: "pre-wrap" }}>
                    {selectedImage.description}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        title={jobToDisable ? `Mark Jobcard #${jobToDisable.serial_number || jobToDisable.id} as done?` : "Mark as done"}
        open={confirmOpen}
        onCancel={() => { setConfirmOpen(false); setJobToDisable(null); }}
        confirmLoading={updateInfo.isLoading}
        onOk={async () => {
          if (!jobToDisable) return;
          try {
            const payload = {
              id: jobToDisable.id,
              is_digitizer_completed: true,
            };
            await updateJobcard(payload).unwrap();
            message.success("Digitizer task completed");
          } catch (e) {
            // Even if backend ignores the flag, optimistically hide it locally
          }
          setConfirmOpen(false);
          setJobToDisable(null);
          refetch();
        }}
        okText="Yes, mark done"
        cancelText="No"
      >
        Are you sure you want to mark this job as done? It will be disabled from this page.
      </Modal>

      <Modal
        title={activeJobcard ? `Update Your Work on Jobcard #${activeJobcard.serial_number || activeJobcard.id}` : ""}
        open={!!activeJobcard}
        onCancel={closeUpdate}
        width={900}
        footer={[
          <Button key="cancel" onClick={closeUpdate}>Cancel</Button>,
          <Button key="upload" type="primary" loading={uploadInfo.isLoading} onClick={doUpload}>Upload</Button>,
        ]}
      >
        {activeJobcard && (
          <>
            {/* Main Image */}
            {activeJobcard.main_image && (
              <>
                <Divider orientation="center">Main Image</Divider>
                <div style={{ marginBottom: 16 }}>
                  <div
                    style={{
                      background: "#fff",
                      borderRadius: 12,
                      padding: 20,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      width: "fit-content",
                    }}
                  >
                    <div
                      style={{ position: "relative", width: 180, height: 180, borderRadius: 18, overflow: "hidden", background: "#f0f0f0" }}
                    >
                      {(() => {
                        const rawSrc = activeJobcard.main_image;
                        const imageUrl = withBase(rawSrc);
                        const lower = String(rawSrc || "").toLowerCase();
                        const isPdf = lower.endsWith('.pdf');
                        const isImage = /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(lower);
                        const ext = getExtFromPath(rawSrc) || "";
                        return (
                          <>
                            {imageUrl ? (
                              isPdf ? (
                                <div style={{ width: "100%", height: "100%" }}>
                                  <iframe
                                    src={`${imageUrl}#toolbar=1&navpanes=0&scrollbar=1`}
                                    title="Main PDF"
                                    style={{ width: "100%", height: "100%", border: 0 }}
                                  />
                                </div>
                              ) : isImage ? (
                                <img src={imageUrl} alt="Main Image" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                              ) : (
                                <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", background: "#f7f7f7" }}>
                                  <span role="img" aria-label="file" style={{ fontSize: 44, lineHeight: 1 }}>🗂️</span>
                                </div>
                              )
                            ) : (
                              <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", background: "#f7f7f7" }}>
                                <span role="img" aria-label="file" style={{ fontSize: 44, lineHeight: 1 }}>🗂️</span>
                              </div>
                            )}
                            <button
                              onClick={() => downloadImage(imageUrl, `${activeJobcard.serial_number || activeJobcard.id}_main_image${ext}`)}
                              style={{
                                position: "absolute",
                                bottom: 10,
                                left: "50%",
                                transform: "translateX(-50%)",
                                border: 0,
                                background: "#ffffff",
                                color: "#111",
                                cursor: "pointer",
                                fontSize: 16,
                                padding: "8px 12px",
                                borderRadius: 999,
                                fontWeight: 600,
                                zIndex: 2,
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                              }}
                            >
                              <DownloadOutlined />
                            </button>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Master Images */}
            <Divider orientation="center">Master Images</Divider>
            <div style={{ marginBottom: 16 }}>
              {(() => {
                const allImages = activeJobcard.jobcard_images || [];
                const designerImages = allImages.filter((img) => getImageUploader(img) === "Designer");
                const digitizerImages = allImages.filter((img) => getImageUploader(img) === "Digitizer");
                const embroidaryImages = allImages.filter((img) => getImageUploader(img) === "Embroidary");

                const renderImageCard = (img, showMeta = false) => {
                  const rawSrc = getImage(img);
                  const imageUrl = withBase(rawSrc);
                  const lower = String(rawSrc || "").toLowerCase();
                  const isPdf = lower.endsWith('.pdf');
                  const isImage = /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(lower);
                  const ext = getExtFromPath(rawSrc) || "";
                  const hasMeta = showMeta && (img?.title || img?.description);
                  return (
                    <div key={img.id} style={{ position: "relative", borderRadius: 12, overflow: "hidden", width: "100%", aspectRatio: "1 / 1", background: "#f0f0f0", boxShadow: "0 6px 16px rgba(0,0,0,0.08)" }}>
                      <div
                        style={{
                          position: "absolute",
                          top: 10,
                          left: 10,
                          background: "rgba(0,0,0,0.55)",
                          color: "#fff",
                          fontSize: 12,
                          padding: "4px 10px",
                          borderRadius: 999,
                          zIndex: 2,
                          fontWeight: 600,
                        }}
                      >
                        {img.side || "Image"}
                      </div>
                      {img.created_at && (
                        <div
                          style={{
                            position: "absolute",
                            top: 10,
                            right: 10,
                            background: "rgba(0,0,0,0.6)",
                            color: "#fff",
                            fontSize: 9,
                            padding: "2px 6px",
                            borderRadius: 4,
                            zIndex: 2,
                            fontWeight: 400,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {formatImageCreatedAt(img.created_at)}
                        </div>
                      )}
                      {imageUrl ? (
                        isPdf ? (
                          <div style={{ width: "100%", height: "100%" }}>
                            <iframe
                              src={`${imageUrl}#toolbar=1&navpanes=0&scrollbar=1`}
                              title={`PDF - ${img.side}`}
                              style={{ width: "100%", height: "100%", border: 0 }}
                            />
                          </div>
                        ) : isImage ? (
                          <img src={imageUrl} alt={img.side} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        ) : (
                          <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", background: "#f7f7f7" }}>
                            <span role="img" aria-label="file" style={{ fontSize: 44, lineHeight: 1 }}>🗂️</span>
                          </div>
                        )
                      ) : (
                        <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", background: "#f7f7f7" }}>
                          <span role="img" aria-label="file" style={{ fontSize: 44, lineHeight: 1 }}>🗂️</span>
                        </div>
                      )}
                      <button
                        onClick={() => downloadImage(imageUrl, `${activeJobcard.serial_number || activeJobcard.id}_${img.side}${ext}`)}
                        style={{
                          position: "absolute",
                          bottom: hasMeta ? 70 : 10,
                          left: "50%",
                          transform: "translateX(-50%)",
                          border: 0,
                          background: "#ffffff",
                          color: "#111",
                          cursor: "pointer",
                          fontSize: 16,
                          padding: "8px 12px",
                          borderRadius: 999,
                          fontWeight: 600,
                          zIndex: 2,
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                        }}
                      >
                        <DownloadOutlined />
                      </button>
                      {hasMeta && (
                        <div
                          style={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            width: "100%",
                            padding: "10px 14px 14px",
                            background: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.75) 100%)",
                            color: "#fff",
                            zIndex: 1,
                          }}
                        >
                          {img.title && (
                            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: img.description ? 4 : 0 }}>
                              {img.title}
                            </div>
                          )}
                          {img.description && (
                            <div style={{ fontSize: 12, opacity: 0.85, maxHeight: 48, overflow: "hidden", textOverflow: "ellipsis" }}>
                              {img.description}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                };

                const renderGroup = (title, images) => {
                  if (!images.length) return null;
                  const gridTemplate = "repeat(auto-fill, minmax(180px, 1fr))";
                  const titleColor = title === "Designer" ? "#1890ff" : title === "Embroidary" ? "#722ed1" : "#52c41a";
                  return (
                    <div style={{ background: "#fff", borderRadius: 12, padding: 20, boxShadow: "0 2px 8px rgba(0,0,0,0.1)", marginBottom: 16 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, paddingBottom: 12, borderBottom: "2px solid #f0f0f0" }}>
                        <div style={{ width: 4, height: 20, background: titleColor, borderRadius: 2 }} />
                        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: "#333" }}>{title}</h2>
                      </div>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: gridTemplate,
                          gap: 16,
                          width: "100%",
                        }}
                      >
                        {images.map((img) => renderImageCard(img, title !== "Designer"))}
                      </div>
                    </div>
                  );
                };

                return (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: 16, alignItems: "start", width: "100%" }}>
                    {renderGroup("Designer", designerImages)}
                    {renderGroup("Digitizer", digitizerImages)}
                    {renderGroup("Embroidary", embroidaryImages)}
                  </div>
                );
              })()}
            </div>

            {/* Embroidery Files */}
            <Divider orientation="center" style={{ marginTop: 16 }}>Embroidery Files</Divider>
            <Row gutter={[24, 24]}>
              {(() => {
                const sideMeta = [
                  { label: "Front", key: "is_front" },
                  { label: "Right Middle", key: "is_right_middle" },
                  { label: "Left Middle", key: "is_left_middle" },
                  { label: "Back", key: "is_back" },
                  { label: "Velcro", key: "is_velcro" },
                  { label: "Visor", key: "is_visor" },
                ];
                const designerSides = new Set(
                  (activeJobcard.jobcard_images || [])
                    .filter(img => img && img.side && getImageUploader(img) === "Designer")
                    .map(img => img.side)
                );
                const digitizerDone = new Set(
                  (activeJobcard.jobcard_images || [])
                    .filter(img => img && img.side && img.is_degitizer_image)
                    .map(img => img.side)
                );
                const embroidaryDone = new Set(
                  (activeJobcard.jobcard_images || [])
                    .filter(img => img && img.side && getImageUploader(img) === "Embroidary")
                    .map(img => img.side)
                );
                return sideMeta
                  .filter(({ label }) => designerSides.has(label) && !embroidaryDone.has(label))
                  .map(({ label, key }) => {
                    const alreadyDone = digitizerDone.has(label);
                    const selected = filesBySide[label];
                    const title = titlesBySide[label] || "";
                    const description = descriptionsBySide[label] || "";

                    const dImg = (activeJobcard?.jobcard_images || []).find(img => img && img.side === label && img.is_degitizer_image);
                    const isRejected = dImg && dImg.description && dImg.description.startsWith("[REJECTED]");
                    const rejectionNote = isRejected ? dImg.description.replace("[REJECTED]", "").trim() : "";

                    // Box styling based on status
                    let borderColor = alreadyDone ? "#eaeaea" : "#bfbfbf";
                    let bgColor = alreadyDone ? "#fafafa" : "#fcfcfc";
                    if (isRejected) {
                      borderColor = "#ff4d4f";
                      bgColor = "#fff1f0";
                    }

                    return (
                      <Col span={8} key={key}>
                        <div style={{ border: `1px ${alreadyDone ? "solid" : "dashed"} ${borderColor}`, borderRadius: 12, background: bgColor, padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
                          <div style={{ fontWeight: 600, color: isRejected ? "#ff4d4f" : "inherit" }}>
                            {label} {isRejected && <span style={{ fontSize: 12, fontWeight: 500 }}>(Rejected)</span>}
                          </div>
                          {isRejected && (
                            <div style={{ color: "#cf1322", fontSize: 12, fontWeight: 500, background: "#ffccc7", padding: "4px 8px", borderRadius: 4 }}>
                              Note: {rejectionNote}
                            </div>
                          )}
                          <Upload.Dragger
                            accept=".emb"
                            beforeUpload={(file) => {
                              handleFileChange(label, file);
                              return false;
                            }}
                            multiple={false}
                            maxCount={1}
                            showUploadList={false}
                            style={{ width: "100%", background: "transparent", padding: 8, border: "none" }}
                          >
                            <div>
                              <UploadOutlined style={{ fontSize: 28, color: selected ? "#52c41a" : "#1677ff" }} />
                              <div style={{ marginTop: 8, color: "#8c8c8c" }}>
                                {selected ? selected.name : (alreadyDone ? "Choose file to replace" : "Click or drag to upload")}
                              </div>
                            </div>
                          </Upload.Dragger>
                          <Input
                            placeholder="Title (optional)"
                            value={title}
                            onChange={(e) => setTitlesBySide((prev) => ({ ...prev, [label]: e.target.value }))}
                          />
                          <Input.TextArea
                            placeholder="Description (optional)"
                            value={description}
                            onChange={(e) => setDescriptionsBySide((prev) => ({ ...prev, [label]: e.target.value }))}
                            rows={2}
                          />
                        </div>
                      </Col>
                    );
                  });
              })()}
            </Row>
          </>
        )}
      </Modal>

      {/* Upload type is auto-set by role (degitizer => true) */}
    </div>
  );
};

export default DigitizerJobcards;


